import React, { useEffect, useMemo, useState } from 'react';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import Accordion from '@mui/material/Accordion';

import type { ILead } from 'src/services/api';
import TabsBar from 'src/components/tabs-bar';
import { AccordionSectionHeader, AccordionSectionDetails } from 'src/components/accordion-section';
import LeadsTablePagination from 'src/pages/landlord/leads/components/table/LeadsTablePagination';
import MobileLeadsTableRow from 'src/pages/landlord/leads/components/table/MobileLeadsTableRow';

const LEADS_ROWS_PER_PAGE = 25;

interface Props {
    title?: string,
    leads?: ILead[],
}

export default function MobileLeadsTable({
    title,
    leads,
}: Props) {
    const [expanded, setExpanded] = useState(true);
    const [page, setPage] = useState(1);

    const numberOfLeads = leads?.length || 0;

    const startIndex = (page - 1) * LEADS_ROWS_PER_PAGE;

    let endIndex = page * LEADS_ROWS_PER_PAGE;
    if (endIndex > numberOfLeads) {
        endIndex = numberOfLeads;
    }

    const currentPageLeads = useMemo(() => {
        if (!leads) {
            return [];
        }

        return leads.slice(startIndex, endIndex);
    }, [leads, page, LEADS_ROWS_PER_PAGE, startIndex, endIndex]);

    // Reset page when leads change
    useEffect(() => {
        setPage(1);
    }, [leads?.map(lead => lead.uuid).join(',')]);

    const table = (
        <TableContainer component={Paper} elevation={0} square>
            <Table sx={{ tableLayout: 'fixed' }}>
                <TableBody>
                    {currentPageLeads.map((lead) => (
                        <MobileLeadsTableRow key={lead.uuid} lead={lead} />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const pagination = (
        <span onClick={e => e.stopPropagation()}>
            <LeadsTablePagination
                leads={leads}
                page={page}
                setPage={setPage}
                rowsPerPage={LEADS_ROWS_PER_PAGE}
            />
        </span>
    );

    if (title) {
        return (
            <Accordion
                disableGutters
                square
                elevation={0}
                expanded={expanded}
                onChange={(_, val) => setExpanded(val)}
                sx={{ width: '100%', '&::before': { display: 'none' } }}
            >
                <AccordionSectionHeader
                    expanded={expanded}
                    title={title}
                    trailing={pagination}
                />
                <AccordionSectionDetails>
                    {table}
                </AccordionSectionDetails>
            </Accordion>
        );
    }

    return (
        <div>
            <TabsBar trailing={pagination}>
                {null}
            </TabsBar>
            {table}
        </div>
    );
}
